import { FixedScoreConsideration } from '../considerations/FixedScoreConsideration';
/**
 * UtilityAI的根节点推理器
 *
 * @description
 * 推理器负责从多个考虑因素中选择最佳的行动方案。
 * 它是效用AI系统的核心组件，通过评估各种考虑因素来做出决策。
 *
 * @template T 上下文类型
 *
 * @example
 * ```typescript
 * class MyReasoner extends Reasoner<GameContext> {
 *   protected selectBestConsideration(context: GameContext): IConsideration<GameContext> {
 *     // 实现选择逻辑
 *     return this._considerations[0];
 *   }
 * }
 *
 * const reasoner = new MyReasoner();
 * reasoner.addConsideration(new AttackConsideration());
 * reasoner.addConsideration(new DefendConsideration());
 *
 * const action = reasoner.select(gameContext);
 * if (action) {
 *   action.execute(gameContext);
 * }
 * ```
 */
export class Reasoner {
    constructor() {
        /**
         * 默认考虑因素，当没有其他考虑因素可用时使用
         * 通常返回一个固定的低分数
         */
        this.defaultConsideration = new FixedScoreConsideration();
        /**
         * 考虑因素列表
         * 推理器将从这些考虑因素中选择最佳的一个
         */
        this._considerations = new Array();
    }
    /**
     * 选择并返回最佳行动
     *
     * @param context 决策上下文
     * @returns 选中的行动，如果没有可用行动则返回null
     */
    select(context) {
        try {
            const consideration = this.selectBestConsideration(context);
            if (consideration != null) {
                return consideration.action;
            }
        }
        catch (error) {
            console.error('选择最佳考虑因素时发生错误:', error);
        }
        return null;
    }
    /**
     * 添加考虑因素到推理器
     *
     * @param consideration 要添加的考虑因素
     * @returns 返回自身以支持链式调用
     * @throws {Error} 当consideration为null或undefined时抛出错误
     */
    addConsideration(consideration) {
        if (consideration == null) {
            throw new Error('考虑因素不能为null或undefined');
        }
        this._considerations.push(consideration);
        return this;
    }
    /**
     * 移除指定的考虑因素
     *
     * @param consideration 要移除的考虑因素
     * @returns 是否成功移除
     */
    removeConsideration(consideration) {
        const index = this._considerations.indexOf(consideration);
        if (index !== -1) {
            this._considerations.splice(index, 1);
            return true;
        }
        return false;
    }
    /**
     * 清空所有考虑因素
     */
    clearConsiderations() {
        this._considerations.length = 0;
    }
    /**
     * 获取考虑因素数量
     *
     * @returns 当前考虑因素的数量
     */
    getConsiderationCount() {
        return this._considerations.length;
    }
    /**
     * 获取所有考虑因素的只读副本
     *
     * @returns 考虑因素数组的副本
     */
    getConsiderations() {
        return [...this._considerations];
    }
    /**
     * 设置默认考虑因素
     *
     * @param defaultConsideration 新的默认考虑因素
     * @returns 返回自身以支持链式调用
     * @throws {Error} 当defaultConsideration为null或undefined时抛出错误
     */
    setDefaultConsideration(defaultConsideration) {
        if (defaultConsideration == null) {
            throw new Error('默认考虑因素不能为null或undefined');
        }
        this.defaultConsideration = defaultConsideration;
        return this;
    }
    /**
     * 检查是否有可用的考虑因素
     *
     * @returns 是否有考虑因素可用
     */
    hasConsiderations() {
        return this._considerations.length > 0;
    }
}
//# sourceMappingURL=Reasoner.js.map